import { getDefaultBuConfig, getDefaultBuConfigElement } from "domain/repository/apiUtil";
import _ from "lodash";
import { CounterCustomerChargeMaintenanceConstant } from "presentation/constant/CounterCustomerCharge/CounterCustomerChargeMaintenanceConstant";
import { stateDropdownOption } from "presentation/constant/DropDownOptions/StaticDropdownOptions";
import { useCounterCustomerChargeMaintenanceVM } from "presentation/hook/CounterCustomerCharge/useCounterCustomerChargeMaintenanceVM";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useCounterCustomerChargeMaintenanceTracked } from "presentation/store/CounterCustomerCharge/CounterCustomerChargeMaintenanceProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DateRangePickerComponent from "presentation/view/components/DateRangePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { memo, useMemo } from "react";
import { FieldType, HPHInputMask, IFieldValue } from "veronica-ui-component/dist/component/core";
const CounterCustomerChargeSearchForm = () => {

    const [counterCustomerChargeState] = useCounterCustomerChargeMaintenanceTracked();
    const counterCustomerChargeVM = useCounterCustomerChargeMaintenanceVM();
    const counterCustomerChargeSearchCriteria = counterCustomerChargeState.searchCriteria;
    const COUNTER_CUSTOMER_CHARGE_CONSTANT = CounterCustomerChargeMaintenanceConstant.Search;
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit } = anaInfoState;
    const railEnable: boolean = getDefaultBuConfigElement<boolean>("REACT_APP_FIELD_ENABLE", "RAIL") || false;
    const isRailMaster: boolean = getDefaultBuConfig<boolean>('REACT_APP_RAIL_ENABLE') || false;

    const memoSubChgTypeOptions = useMemo(() => {
        if (!counterCustomerChargeSearchCriteria.chgTypeList || counterCustomerChargeSearchCriteria.chgTypeList.length === 0) {
            const allCommonSubChgTypeOptions = Object.values(counterCustomerChargeState.dynamicOptions.subChgTypeDropdownOptions).flat();
            return _.orderBy(_.uniqBy(_.flatMap(_.concat(allCommonSubChgTypeOptions ?? [])), 'value'), 'dropdownLabel');
        }
        const groupOfSubChgTypeOptions = counterCustomerChargeSearchCriteria.chgTypeList?.map(chgType => counterCustomerChargeState.dynamicOptions.subChgTypeDropdownOptions[chgType])
        const commonSubChgTypeOptions = _.orderBy(_.uniqBy(_.flatMap(_.concat(groupOfSubChgTypeOptions ?? [])), 'value'), 'dropdownLabel');
        return commonSubChgTypeOptions;
    }, [counterCustomerChargeSearchCriteria.chgTypeList, counterCustomerChargeState.dynamicOptions.subChgTypeDropdownOptions]);

    const memoSubServiceRequestCodeOptions = useMemo(() => {
        const subServiceRequestCodeOptions = counterCustomerChargeSearchCriteria.serviceRequestCode ? counterCustomerChargeState.dynamicOptions.subServiceRequestCodeDropdownOptions[counterCustomerChargeSearchCriteria.serviceRequestCode] : counterCustomerChargeState.dynamicOptions.allSubReqServiceCodeDropdownOptions;
        return subServiceRequestCodeOptions;
    }, [counterCustomerChargeSearchCriteria.serviceRequestCode, counterCustomerChargeState.dynamicOptions.allSubReqServiceCodeDropdownOptions, counterCustomerChargeState.dynamicOptions.subServiceRequestCodeDropdownOptions])


    const memoChgType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "265px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={counterCustomerChargeSearchCriteria.chgTypeList?.join(',')}
                fieldValue={counterCustomerChargeSearchCriteria?.chgTypeList}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.CHARGE_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'chgTypeList'}
                maxLength={60}
                isMultipleDropDown={true}
                options={counterCustomerChargeState.dynamicOptions.chgTypeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [counterCustomerChargeSearchCriteria.chgTypeList, counterCustomerChargeState.dynamicOptions.chgTypeDropdownOptions, counterCustomerChargeVM, COUNTER_CUSTOMER_CHARGE_CONSTANT.CHARGE_TYPE])

    const memoSubChgType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "265px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={counterCustomerChargeSearchCriteria.subChgTypeList?.join(',')}
                fieldValue={counterCustomerChargeSearchCriteria?.subChgTypeList}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.SUB_CHARGE_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'subChgTypeList'}
                maxLength={60}
                isMultipleDropDown={true}
                options={memoSubChgTypeOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [counterCustomerChargeSearchCriteria.subChgTypeList, counterCustomerChargeVM, memoSubChgTypeOptions, COUNTER_CUSTOMER_CHARGE_CONSTANT.SUB_CHARGE_TYPE])

    const memoOpsLine = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "265px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={counterCustomerChargeSearchCriteria.opsLines?.join(',')}
                fieldValue={counterCustomerChargeSearchCriteria?.opsLines}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.OPS_LINE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'opsLines'}
                maxLength={60}
                isMultipleDropDown={true}
                options={counterCustomerChargeState.dynamicOptions.opsLineDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [COUNTER_CUSTOMER_CHARGE_CONSTANT.OPS_LINE, counterCustomerChargeSearchCriteria.opsLines, counterCustomerChargeState.dynamicOptions.opsLineDropdownOptions, counterCustomerChargeVM])

    const memoOpsDateFromTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "265px", marginBottom: "24px" }}>
            <DateRangePickerComponent
                label={COUNTER_CUSTOMER_CHARGE_CONSTANT.OPS_DATE_RANGE}
                width="265px"
                fieldNames={{ startField: "opsDateFrom", endField: "opsDateTo" }}
                dateRange={{ startDate: counterCustomerChargeSearchCriteria.opsDateFrom, endDate: counterCustomerChargeSearchCriteria.opsDateTo }}
                onDatesChange={counterCustomerChargeVM.onDateRangeChange}
            />
        </div>
        , [counterCustomerChargeSearchCriteria.opsDateFrom, counterCustomerChargeSearchCriteria.opsDateTo, counterCustomerChargeVM, COUNTER_CUSTOMER_CHARGE_CONSTANT.OPS_DATE_RANGE])

    const memoCoVslVoy = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "560px", marginBottom: "24px" }}>
            <HPHInputMask
                label={COUNTER_CUSTOMER_CHARGE_CONSTANT.CONS_VSL_VOY}
                width={'560px'}
                value={{
                    co: counterCustomerChargeSearchCriteria.co ?? '',
                    vsl: counterCustomerChargeSearchCriteria.vsl ?? '',
                    voy: counterCustomerChargeSearchCriteria.voy ?? '',
                }}
                fieldConfiguration={{
                    co: { maxLength: 4, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                    vsl: { maxLength: 10, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                    voy: { maxLength: 10, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                }}
                onDataChange={(e: any) => counterCustomerChargeVM.onCoVslVoyChange(e, {
                    co: 'co',
                    vsl: 'vsl',
                    voy: 'voy',
                })} />
        </div>
        , [COUNTER_CUSTOMER_CHARGE_CONSTANT.CONS_VSL_VOY, counterCustomerChargeSearchCriteria.co, counterCustomerChargeSearchCriteria.vsl, counterCustomerChargeSearchCriteria.voy, counterCustomerChargeVM])

    const memoCntrNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "855px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                helpTextElement={"Use line break ( Enter ) as delimiter"}
                readOnlyValue={counterCustomerChargeSearchCriteria.cntrList?.join('\n') || ''}
                fieldValue={counterCustomerChargeSearchCriteria?.cntrList?.join('\n')}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.CNTR_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXTAREA}
                fieldKey={'cntrList'}
                maxLength={780}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onTextAreaChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [counterCustomerChargeSearchCriteria.cntrList, counterCustomerChargeVM, COUNTER_CUSTOMER_CHARGE_CONSTANT.CNTR_NO])

    const memoChargeOnCompany = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "265px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={counterCustomerChargeSearchCriteria.chgOnCompList?.join(',')}
                fieldValue={counterCustomerChargeSearchCriteria?.chgOnCompList}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.CHARGE_ON_COMPANY}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'chgOnCompList'}
                maxLength={60}
                isMultipleDropDown={true}
                options={counterCustomerChargeState.dynamicOptions.chgOnCompDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [counterCustomerChargeSearchCriteria.chgOnCompList, counterCustomerChargeVM, counterCustomerChargeState.dynamicOptions.chgOnCompDropdownOptions, COUNTER_CUSTOMER_CHARGE_CONSTANT.CHARGE_ON_COMPANY])

    const memoBillToCompany = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "265px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={counterCustomerChargeSearchCriteria.billToCompList?.join(',')}
                fieldValue={counterCustomerChargeSearchCriteria?.billToCompList}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.BILL_TO_COMPANY}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'billToCompList'}
                maxLength={60}
                isMultipleDropDown={true}
                options={counterCustomerChargeState.dynamicOptions.billToCompDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [counterCustomerChargeSearchCriteria.billToCompList, counterCustomerChargeVM, counterCustomerChargeState.dynamicOptions.billToCompDropdownOptions, COUNTER_CUSTOMER_CHARGE_CONSTANT.BILL_TO_COMPANY])

    const memoBillOfLading = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "265px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={counterCustomerChargeSearchCriteria?.billOfLadingNo || ''}
                fieldValue={counterCustomerChargeSearchCriteria?.billOfLadingNo}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.BILL_OF_LADING}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'billOfLadingNo'}
                maxLength={35}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onTextAreaChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [counterCustomerChargeSearchCriteria.billOfLadingNo, counterCustomerChargeVM, COUNTER_CUSTOMER_CHARGE_CONSTANT.BILL_OF_LADING])

    const memoShippingBill = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "265px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={counterCustomerChargeSearchCriteria?.shippingBill || ''}
                fieldValue={counterCustomerChargeSearchCriteria?.shippingBill}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.SHIPPING_BILL}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'shippingBill'}
                maxLength={35}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onTextAreaChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [counterCustomerChargeSearchCriteria.shippingBill, counterCustomerChargeVM, COUNTER_CUSTOMER_CHARGE_CONSTANT.SHIPPING_BILL])

    const memoAgentNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "265px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={counterCustomerChargeSearchCriteria?.agentNo || ''}
                fieldValue={counterCustomerChargeSearchCriteria?.agentNo}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.AGENT_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'agentNo'}
                maxLength={60}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onTextAreaChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [counterCustomerChargeSearchCriteria.agentNo, counterCustomerChargeVM, COUNTER_CUSTOMER_CHARGE_CONSTANT.AGENT_NO])

    const memoState = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "265px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={counterCustomerChargeSearchCriteria.states?.join(',')}
                fieldValue={counterCustomerChargeSearchCriteria?.states}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.STATE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'states'}
                maxLength={60}
                isMultipleDropDown={true}
                options={stateDropdownOption}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [counterCustomerChargeSearchCriteria.states, counterCustomerChargeVM, COUNTER_CUSTOMER_CHARGE_CONSTANT.STATE])

    const memoServiceRequestCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "265px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={counterCustomerChargeSearchCriteria?.serviceRequestCode ?? ''}
                fieldValue={counterCustomerChargeSearchCriteria?.serviceRequestCode}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.SERVICE_REQUEST_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'serviceRequestCode'}
                maxLength={60}
                options={counterCustomerChargeState.dynamicOptions.serviceRequestCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [counterCustomerChargeSearchCriteria.serviceRequestCode, counterCustomerChargeVM, COUNTER_CUSTOMER_CHARGE_CONSTANT.SERVICE_REQUEST_CODE, counterCustomerChargeState.dynamicOptions.serviceRequestCodeDropdownOptions])

    const memoSubServiceRequestCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "265px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={counterCustomerChargeSearchCriteria?.subServiceRequestCode ?? ''}
                fieldValue={counterCustomerChargeSearchCriteria?.subServiceRequestCode}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.SUB_SERVICE_REQUEST_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'subServiceRequestCode'}
                maxLength={60}
                options={memoSubServiceRequestCodeOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [counterCustomerChargeSearchCriteria.subServiceRequestCode, counterCustomerChargeVM, COUNTER_CUSTOMER_CHARGE_CONSTANT.SUB_SERVICE_REQUEST_CODE, memoSubServiceRequestCodeOptions])

    const memoServiceRequestRef = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "265px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={counterCustomerChargeSearchCriteria?.serviceRequestRef || ''}
                fieldValue={counterCustomerChargeSearchCriteria?.serviceRequestRef}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.SERVICE_REQUEST_REF}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'serviceRequestRef'}
                maxLength={60}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onTextAreaChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [counterCustomerChargeSearchCriteria.serviceRequestRef, counterCustomerChargeVM, COUNTER_CUSTOMER_CHARGE_CONSTANT.SERVICE_REQUEST_REF])

    const memoTrainID = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "265px", marginBottom: "24px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={counterCustomerChargeSearchCriteria?.trainId || ''}
                fieldValue={counterCustomerChargeSearchCriteria?.trainId}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.TRAIN_ID}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'trainId'}
                maxLength={35}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onTextAreaChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [counterCustomerChargeSearchCriteria.trainId, counterCustomerChargeVM, COUNTER_CUSTOMER_CHARGE_CONSTANT.TRAIN_ID])

    return <>
        <CriteriaItemContainer>
            {memoChgType}
            {memoSubChgType}
            {memoState}
        </CriteriaItemContainer>
        <CriteriaItemContainer>
            {memoOpsDateFromTo}
            {memoChargeOnCompany}
            {memoBillToCompany}
        </CriteriaItemContainer>
        <CriteriaItemContainer>
            {memoCoVslVoy}
            {memoOpsLine}
        </CriteriaItemContainer>
        <CriteriaItemContainer>
            {memoCntrNo}
        </CriteriaItemContainer>
        <CriteriaItemContainer>
            {memoBillOfLading}
            {memoShippingBill}
            {memoAgentNo}
        </CriteriaItemContainer>

        {(operatingUnit !== 'AQCT' && operatingUnit !== 'RSCT') && <>
            <CriteriaItemContainer>
                {memoServiceRequestRef}
                {memoServiceRequestCode}
                {memoSubServiceRequestCode}
            </CriteriaItemContainer>
        </>}

        {isRailMaster && railEnable && <>
            <CriteriaItemContainer>
                {memoTrainID}
            </CriteriaItemContainer>
        </>}

    </>;
}
export default memo(CounterCustomerChargeSearchForm);
